import { BaseMode } from '../BaseMode.js';
import {
  ORIENTATION_PHASES,
  ORIENTATION_SUB_STATES,
  ORIENTATION_CONTENT,
  PHASE_HEADERS,
  PHASE_TO_STEP,
  STEP_TO_PHASE,
  QUESTIONNAIRE_IDS,
  DIVERSION_CONTENT,
  DEVIATION_BUTTONS,
  interpolateContent,
  getNextPhase,
  ORIENTATION_PROGRESS_STEPS,
  ORIENTATION_TOTAL_STEPS
} from '../orientationContent.js';
import { normalizeFirstName } from '../../utils/nameFormat.js';

/**
 * Valid actions per phase — used to reject stale button clicks from prior phases.
 * Layer 2 of stale button protection (Layer 1 is UI disabling in _advancePhase).
 */
const VALID_ACTIONS_BY_PHASE = {
  INTRODUCTION: ['continue', 'confirm_name', 'decline_name', 'continue_setup'],
  DISCOVERY: ['continue', 'skip_love_languages', 'begin_questionnaire', 'continue_setup'],
  INSIGHT: ['continue', 'confirm_insight', 'deny_insight', 'discuss_insight', 'continue_setup'],
  ACCOUNT_PROMPT: ['continue', 'create_account', 'defer_account', 'account_created', 'continue_setup'],
  // `complete_foundation` is the questionnaire-skipped fork's primary action
  // (see orientationContent.js TRANSITION `skippedButtonGroups`).
  TRANSITION: ['scenario_love_language', 'explore_own', 'complete_foundation', 'continue_setup']
};

/**
 * Actions valid in any phase — global affordances (e.g., dismiss) that
 * intentionally bypass the per-phase stale-button check. The header close
 * button is always-available by construction (it never routes through the
 * action dispatcher); listing `dismiss` here gives the inline Exit button
 * the same always-available semantics without duplicating it across every
 * phase's allowlist.
 */
const GLOBAL_VALID_ACTIONS = ['dismiss'];

// Bounded retry budget for persisting onboarding state (audit batch 08 #59).
// Total attempts including the first; backoff grows linearly per attempt.
const ORIENTATION_PERSIST_ATTEMPTS = 3;

/**
 * How long the completion beat is held before orientation actually exits.
 * Covers the progress strip's 0.3s width transition (header.css
 * `.header-linear-progress__fill`) plus enough dwell for 100% to register as a
 * finished state rather than a flicker. Runs concurrently with the
 * completion network call, so it costs no wall-clock the user was not already
 * spending.
 */
const ORIENTATION_COMPLETE_BEAT_MS = 600;
const ORIENTATION_PERSIST_RETRY_DELAY_MS = 500;

/**
 * Illustrative 12-topic map for the orientation memory-update card, in the
 * registry's display order with backend units (0-100). Nothing here is fetched
 * or personal — the demo card exists to show the feature, and without a
 * `<topics>` payload its mini map would render eleven empty slivers and read as
 * a bug rather than as a map with room to grow. The card's own wedge is driven
 * by `<from>`/`<progress>` (a fixed 0 -> 0.40 sweep), not by this array.
 */
const DEMO_UNDERSTANDING_TOPICS = JSON.stringify([
  { id: 'personality', label: 'Personality', progress: 28 },
  { id: 'sense-of-self', label: 'Sense of Self', progress: 12 },
  { id: 'inner-life', label: 'Inner Life', progress: 20 },
  { id: 'connection', label: 'Connection', progress: 35 },
  { id: 'communication-conflict', label: 'Communication & Conflict', progress: 18 },
  { id: 'sex', label: 'Sex', progress: 0 },
  { id: 'trust-repair', label: 'Trust & Repair', progress: 10 },
  { id: 'values-daily-life', label: 'Values & Daily Life', progress: 22 },
  { id: 'money', label: 'Money', progress: 0 },
  { id: 'family-parenting', label: 'Family & Parenting', progress: 15 },
  { id: 'work-life-stress', label: 'Work & Life Stress', progress: 8 },
  { id: 'grow-play', label: 'Growth & Play', progress: 0 }
]);

/**
 * Relocation options applied to every orientation step/deviation button group so
 * they mount in the chat input (like the rest of the site) instead of the
 * transcript. Resolved by `MessageList.addInteractiveComponent` via `metadata.data`.
 * `groupId` is a stable, shared id — only one orientation group shows at a time, so
 * `Input.showButtons` idempotency replaces the prior group; the registered
 * `orientation` ActionRouter handler routes clicks through the central teardown.
 * `allowTextEntry` keeps the textarea reachable for typed deviations (hybrid path).
 * Echo (each click leaves a user-message bubble of its label) is resolved at click
 * time from the catalog `orientation-step` entry (`button-groups.js`), not here.
 */
const ORIENTATION_INPUT_BUTTON_OPTIONS = {
  renderLocation: 'input',
  allowTextEntry: true,
  groupId: 'orientation-step'
};

/**
 * Options for the questionnaire-intro Begin/Skip group. Distinct groupId from
 * `orientation-step` on purpose: that group is echo:true at the group level, which
 * would also bubble "Begin questionnaire" (it only opens the panel). Echo is resolved
 * at click time from the catalog `orientation-questionnaire-actions` entry
 * (`button-groups.js`), which mirrors the discovery twin: group-level false, Skip
 * alone echoing.
 */
const ORIENTATION_QUESTIONNAIRE_BUTTON_OPTIONS = {
  renderLocation: 'input',
  allowTextEntry: true,
  groupId: 'orientation-questionnaire-actions'
};

/**
 * OrientationMode - Guides new users through the onboarding flow
 *
 * A 5-phase onboarding experience (v2) that:
 * - Captures user name and introduces the platform
 * - Has them complete the Love Languages questionnaire
 * - Shows their first insight and prompts for account creation
 * - Transitions them to self-directed exploration
 *
 * Uses Approach C (Hybrid): All interactions handled within OrientationMode
 * without mode switching. User stays in ORIENTATION mode throughout.
 */
export class OrientationMode extends BaseMode {
  /**
   * OrientationMode manages its own thinking indicator.
   * The orchestrator should not auto-activate it during mode entry or message handling.
   */
  static managesOwnThinkingIndicator = true;

  /**
   * Input placeholders for different orientation states
   */
  static PLACEHOLDERS = {
    NAME_INPUT: 'Type your firstname...',
    SITUATION_INPUT: 'Describe the situation...',
    DEFAULT: 'Reply...',
  };

  constructor() {
    super({
      name: 'ORIENTATION',
      contextBuilder: null
    });

    // === Persisted State (rebuilt from onboardingData on resume) ===
    this.onboardingData = {
      firstName: null,
      loveLangsInsightId: null,
      branch: null, // 'account' | 'defer'
      skippedLoveLanguages: false,
      insightConfirmed: null // true (confirmed), false (denied), null (not yet answered)
    };

    // === Ephemeral State (reset on page reload) ===
    this.currentPhase = ORIENTATION_PHASES.INTRODUCTION;
    this.currentStepIndex = 0;
    this.subState = null;

    // Set by _rebuildEphemeralState when resuming into a just-converted account;
    // consumed once by _renderCurrentStep to render the ACCOUNT_SETUP beat.
    this._showAccountBeat = false;

    // Phase-specific progress
    this.questionnaireActive = false;
    this.insightDisplayed = false;

    // Cached references
    this.currentInsight = null;
    this.discussionExchanges = 0;

    // Event handlers (bound for cleanup)
    this._boundHandlers = {};

    // Re-entrance guard for phase transitions
    this._isAdvancingPhase = false;

    // Persist-failure notice state: one notice per failure streak, reset on
    // success. Delay is an instance field so tests can zero it.
    this._persistFailureNotified = false;
    this._persistRetryDelayMs = ORIENTATION_PERSIST_RETRY_DELAY_MS;

    // Track if user skipped name step (SSO users with firstName already set)
    this._skippedNameStep = false;

    // Track if resuming orientation (to use 'return' message variant on first render)
    this._isResuming = false;

    // Track if welcome step attention has been triggered (to avoid re-triggering)
    this._welcomeAttentionTriggered = false;

    // Mirrors the last _emitUIState call. Gates the header strategy's strip
    // percent: the name-capture screen hides the header but not .main, and the
    // strip (a child of .main) must appear together with the header, not before.
    this._headerVisible = false;

    // Track previous progress step for animation (null = first render, no animation)
    this._previousProgressStep = null;

    // Set once orientation starts exiting (advice handoff, skip-and-exit, or
    // natural end of TRANSITION). Overrides the header strategy's step-derived
    // percent with a terminal 100 — see _emitCompletionBeat.
    this._completing = false;

    // === Deviation Handling State ===
    // History of deviation exchanges (for LLM context)
    this.deviationHistory = [];

    // Re-entrance guard for deviation handling
    this._isHandlingDeviation = false;

    // === Name Validation State ===
    // Tracks name validation flow: 'confirming_name' | 'no_name_given' | null
    this.nameValidationState = null;
    // The name being confirmed (for 'confirming_name' state)
    this.pendingNameValue = null;
  }

  // ========================================
  // LIFECYCLE HOOKS
  // ========================================

  /**
   * Called when entering OrientationMode
   * Restores state from persisted data if resuming
   */
  async onEnter(previousMode, transitionData = {}) {
    console.log('[OrientationMode] Entering from', previousMode?.name, transitionData);

    // Disable thinking indicator by default during orientation
    // It will be explicitly enabled for specific operations (insight generation pause, etc.)
    this._hideThinkingIndicator();

    // Clear the completion beat on entry as well as exit: _completeOrientation
    // reaches the dashboard via returnToDashboard(), which skips onExit, so a
    // dismiss → resume cycle would otherwise re-enter with a pinned 100%.
    this._completing = false;

    const { resumeFrom, onboardingData } = transitionData;

    // Restore persisted state if resuming
    if (onboardingData) {
      this.onboardingData = { ...this.onboardingData, ...onboardingData };
    }

    // Determine starting phase
    if (resumeFrom && STEP_TO_PHASE[resumeFrom]) {
      this.currentPhase = STEP_TO_PHASE[resumeFrom];
      console.log('[OrientationMode] Resuming from phase:', this.currentPhase);

      // Set resuming flag - used to select 'return' message variants on first render
      this._isResuming = true;

      // Rebuild ephemeral state based on persisted data
      await this._rebuildEphemeralState();

      // Emit phase change to refresh header with correct progress step
      // (Header may have rendered before onEnter set the correct phase)
      if (window.modeOrchestrator?.eventBus) {
        window.modeOrchestrator.eventBus.emit('orientation:phase-changed', {
          phase: this.currentPhase
        });
      }
    } else {
      this.currentPhase = ORIENTATION_PHASES.INTRODUCTION;
      // If user already has firstName (e.g., from SSO), skip name input step
      // Start at Step 1 (acknowledge) instead of Step 0 (name input)
      if (this.onboardingData.firstName) {
        this.currentStepIndex = 1;
        this._skippedNameStep = true;
        console.log('[OrientationMode] User has firstName, skipping name input step');
      } else {
        this.currentStepIndex = 0;
      }
    }

    // Set up event listeners
    this._setupEventListeners();

    // Set custom placeholder for current state
    this._updatePlaceholder();

    // Determine initial UI state based on phase
    // Guest at very start: Phase 1, Step 0, no name
    const isGuestStart = this.currentPhase === ORIENTATION_PHASES.INTRODUCTION &&
                         this.currentStepIndex === 0 &&
                         !this.onboardingData.firstName;

    // SSO user at very start: Phase 1, Step 1 (skipped name input), has name, fresh start
    const isSSOStart = this.currentPhase === ORIENTATION_PHASES.INTRODUCTION &&
                       this.currentStepIndex === 1 &&
                       this.onboardingData.firstName &&
                       !resumeFrom;

    // User at acknowledge step (Step 1) - either SSO fresh start or resuming after name entry
    const isAtAcknowledgeStep = this.currentPhase === ORIENTATION_PHASES.INTRODUCTION &&
                                this.currentStepIndex === 1;

    if (isGuestStart) {
      // Set initial UI state (no header, show logo)
      this._emitUIState('initial');
      // Render the logo in the chat area
      this._renderOrientationLogo();
    } else if (isAtAcknowledgeStep) {
      // At acknowledge step (Step 1) - show header since name step is complete
      this._emitUIState('chat');
      if (isSSOStart) {
        // SSO user starting fresh - show logo (they haven't seen it yet)
        this._renderOrientationLogo();
      }
    } else if (!this.onboardingData.firstName) {
      // Still need name but resuming at Step 0 - show input at bottom, header hidden
      this._emitUIState('initial');
    } else {
      // Past Step 1, show normal chat state with header visible
      this._emitUIState('chat');
    }

    // Header close button is owned by OrientationCloseManager — visibility is
    // driven by FeatureToggleService (toggle:closeButton). No DOM binding here.

    // Return event to trigger initial render
    return {
      event: 'orientation_enter',
      phase: this.currentPhase,
      step: this.currentStepIndex
    };
  }

  /**
   * Called when exiting OrientationMode
   */
  async onExit(nextMode, transitionData = {}) {
    console.log('[OrientationMode] Exiting to', nextMode?.name || 'none');

    // Clean up event listeners
    this._cleanupEventListeners();

    // Reset ephemeral state
    this.currentStepIndex = 0;
    this.subState = null;
    this.questionnaireActive = false;
    this.insightDisplayed = false;
    this.currentInsight = null;
    this.discussionExchanges = 0;

    // Reset deviation state
    this.deviationHistory = [];
    this._isHandlingDeviation = false;

    // Reset name validation state
    this.nameValidationState = null;
    this.pendingNameValue = null;

    // Reset attention trigger flag
    this._welcomeAttentionTriggered = false;

    // Reset progress tracking for clean re-entry
    this._previousProgressStep = null;
    this._completing = false;
  }

  // ========================================
  // MESSAGE ROUTING
  // ========================================

  /**
   * Determine routing strategy for this message
   * Orientation uses programmatic routing for structured flow,
   * with hybrid (LLM) routing for unexpected user input
   */
  getRoutingStrategy(message, context) {
    // Existing sub-state checks for LLM routing
    if (this.subState === ORIENTATION_SUB_STATES.HANDLING_ADVICE) {
      return 'llm';
    }

    if (this.subState === ORIENTATION_SUB_STATES.DISCUSSING_INSIGHT) {
      return 'llm';
    }

    // Check if input is expected for current step
    if (this._isExpectedInput(message)) {
      return 'programmatic';
    }

    // Unexpected input with content -> hybrid (LLM handles deviation)
    if (message?.trim() && this.currentPhase) {
      return 'hybrid';
    }

    // Default to programmatic (button clicks, empty messages)
    return 'programmatic';
  }

  /**
   * Check if the user input is expected for the current step
   * Expected inputs: name in Phase 1, situation in Phase 8
   * @private
   */
  _isExpectedInput(message) {
    if (!message?.trim()) {
      // Empty messages are expected (button clicks trigger these)
      return true;
    }

    // In name validation state, user can type a new name
    if ((this.nameValidationState === 'confirming_name' ||
         this.nameValidationState === 'no_name_given') &&
        message?.trim()) {
      return true;
    }

    // Phase 1, Step 0: Expecting name input
    if (this.currentPhase === ORIENTATION_PHASES.INTRODUCTION &&
        this.currentStepIndex === 0 &&
        !this.onboardingData.firstName) {
      // Any text input at this step is expected as a name
      return true;
    }

    // All other text input is unexpected
    return false;
  }

  /**
   * Generate programmatic response for orientation flow
   */
  async generateProgrammaticResponse(message, context) {
    // Handle re-validation when user types during name confirmation/no-name states
    if (this.nameValidationState && message?.trim()) {
      // User typed a new name - re-validate
      return await this._handleNameInput(message.trim());
    }

    // Handle name input in Phase 1
    if (this.currentPhase === ORIENTATION_PHASES.INTRODUCTION &&
        this.currentStepIndex === 0 && message?.trim()) {
      return await this._handleNameInput(message.trim());
    }

    // Handle orientation entry event
    if (context?.event === 'orientation_enter') {
      return await this._renderCurrentStep();
    }

    // Handle button actions
    if (context?.metadata?.action) {
      return await this._handleButtonAction(context.metadata.action);
    }

    // Fallback: render current step
    return await this._renderCurrentStep();
  }

  /**
   * Post-process response (no-op for orientation)
   */
  postprocessResponse(response, context) {
    return { text: response, actions: [], metadata: {} };
  }

  /**
   * Generate hybrid response for user deviations
   * Calls backend LLM service to handle unexpected input
   */
  async generateHybridResponse(message, context) {
    return await this._handleDeviation(message);
  }

  /**
   * Handle user deviation via LLM streaming
   * @private
   */
  async _handleDeviation(userMessage) {
    // Re-entrance guard - prevent concurrent deviation calls
    if (this._isHandlingDeviation) {
      console.log('[OrientationMode] Deviation already in progress, ignoring');
      return { type: 'instant', text: '' };
    }

    this._isHandlingDeviation = true;
    this._showThinkingIndicator('Thinking...');

    try {
      const { OrientationFlowClient } = await import('../../services/OrientationFlowClient.js');
      const flowClient = new OrientationFlowClient();

      // Build user state for context (enriched for stage-specific prompt variants)
      const userState = {
        firstName: this.onboardingData.firstName || null,
        branch: this.onboardingData.branch || null,
        skippedLoveLanguages: this.onboardingData.skippedLoveLanguages || false,
        questionnaireActive: this.questionnaireActive
      };

      // Get stream from backend
      const stream = flowClient.handleDeviation({
        userMessage,
        phase: this.currentPhase,
        step: this.currentStepIndex,
        userState,
        deviationHistory: this.deviationHistory
      });

      let firstChunk = true;
      let fullText = '';
      let completeData = null;

      // Process stream
      for await (const event of stream) {
        if (event.type === 'chunk') {
          if (firstChunk) {
            this._hideThinkingIndicator();
            // Start streaming message in chat
            this._startStreamingMessage();
            firstChunk = false;
          }
          fullText += event.content;
          this._streamChunkToChat(event.content);
        } else if (event.type === 'complete') {
          completeData = event;
        } else if (event.type === 'error') {
          console.error('[OrientationMode] Deviation stream error:', event.error);
          this._hideThinkingIndicator();
          return this._getFallbackDeviationResponse();
        }
      }

      // Finalize streaming message
      this._finalizeStreamingMessage();

      // Add to deviation history
      this.deviationHistory.push(
        { role: 'user', content: userMessage },
        { role: 'assistant', content: fullText }
      );

      // Limit history to 6 messages (3 exchanges)
      if (this.deviationHistory.length > 6) {
        this.deviationHistory = this.deviationHistory.slice(-6);
      }

      // Handle extracted data (e.g., name)
      if (completeData?.extractedData?.type === 'name' && completeData.extractedData.value) {
        await this._handleExtractedName(completeData.extractedData.value);
      }

      // Render stage-specific components after stream complete
      if (completeData?.components?.length) {
        await this._renderDeviationComponents(completeData.components);
      }

      // If shouldAdvance, auto-advance after a short delay
      if (completeData?.shouldAdvance) {
        setTimeout(() => {
          this._advanceStep();
        }, 1000);
      }

      // Return empty response since we already streamed content
      return { type: 'instant', text: '' };

    } catch (error) {
      console.error('[OrientationMode] Deviation handling failed:', error);
      this._hideThinkingIndicator();
      return this._getFallbackDeviationResponse();
    } finally {
      this._isHandlingDeviation = false;
    }
  }

  /**
   * Start a new streaming message in the chat
   * @private
   */
  _startStreamingMessage() {
    if (window.app?.modules?.chatManager) {
      window.app.modules.chatManager.startStreamingMessage({
        role: 'assistant',
        customClass: 'message--orientation'
      });
    }
  }

  /**
   * Stream a chunk of text to the current streaming message
   * @private
   */
  _streamChunkToChat(content) {
    if (window.app?.modules?.chatManager) {
      window.app.modules.chatManager.appendToStreamingMessage(content);
    }
  }

  /**
   * Finalize the streaming message
   * @private
   */
  _finalizeStreamingMessage() {
    if (window.app?.modules?.chatManager) {
      window.app.modules.chatManager.finalizeStreamingMessage();
    }
  }

  /**
   * Render stage-specific components after a deviation response.
   * Handles buttons, questionnaire artifacts, and insight artifacts.
   *
   * @param {Array} components - Component descriptors from backend
   * @private
   */
  async _renderDeviationComponents(components) {
    if (!components?.length) return;

    for (const component of components) {
      switch (component.type) {
        case 'buttons':
          await this._renderDeviationButtonComponent(component);
          break;
        case 'questionnaire_artifact':
          await this._renderDeviationQuestionnaireArtifact(component);
          break;
        case 'insight_artifact':
          this._renderDeviationInsightArtifact(component);
          break;
      }
    }
  }

  /**
   * Render a buttons component from deviation response
   * @param {Object} component - { type: 'buttons', buttons: [...] }
   * @private
   */
  async _renderDeviationButtonComponent(component) {
    if (!window.modeOrchestrator?.renderComponents) return;

    const renderableComponents = [{
      type: 'buttons',
      buttons: component.buttons.map(btn => {
        // Skip message-type items (they render as text hints, not buttons)
        if (btn.type === 'message') {
          return btn;
        }
        return {
          label: btn.label,
          action: `orientation:${btn.action}`,
          data: { action: btn.action },
          metadata: btn.metadata
        };
      }),
      options: ORIENTATION_INPUT_BUTTON_OPTIONS
    }];

    await window.modeOrchestrator.renderComponents(renderableComponents, {});
  }

  /**
   * Render a questionnaire artifact from deviation response.
   * Re-emits the questionnaire-ready event to re-display the artifact card.
   * @param {Object} component - { type: 'questionnaire_artifact', questionnaireId: string }
   * @private
   */
  async _renderDeviationQuestionnaireArtifact(component) {
    // Load and display the questionnaire artifact (no delay since deviation already streamed).
    // Pass skipPostButtons=true to prevent duplicate skip buttons — STAGE_COMPONENTS
    // already includes the skip button, so postArtifactButtons must not render its own.
    await this._loadQuestionnaireArtifact(component.questionnaireId, 0, { skipPostButtons: true });
  }

  /**
   * Render an insight artifact from deviation response.
   * Emits directly via component:artifact-requested — does NOT delegate to
   * _emitInsightArtifact() which would duplicate buttons via postArtifactButtons.
   * @param {Object} component - { type: 'insight_artifact', insightType: string, isGeneric: boolean }
   * @private
   */
  _renderDeviationInsightArtifact(component) {
    if (!this.currentInsight) {
      console.warn('[OrientationMode] Cannot render insight artifact — currentInsight is null');
      return;
    }
    if (!window.modeOrchestrator?.eventBus) return;

    window.modeOrchestrator.eventBus.emit('component:artifact-requested', {
      type: 'insight',
      data: {
        status: 'ready',
        title: this.currentInsight.title || 'Your insight',
        insightId: this.currentInsight.id,
        content: this.currentInsight.content,
        insightType: this.currentInsight.insightType || 'individual',
        insightCode: this.currentInsight.insightCode,
        pathId: 'orientation',
        isGeneric: component.isGeneric || false
      }
    });
  }

  /**
   * Handle extracted name from deviation response
   * @private
   */
  async _handleExtractedName(rawName) {
    // Normalize before anything reads it, so the header, the greeting, and the
    // persisted row all show the same casing.
    const name = normalizeFirstName(rawName);
    this.onboardingData.firstName = name;

    // Persist name via API
    try {
      const { API } = await import('../../core/api.js');
      await API.captureGuestName(name);
    } catch (error) {
      console.error('[OrientationMode] Failed to persist extracted name:', error);
    }

    // Emit name captured event
    if (window.modeOrchestrator?.eventBus) {
      window.modeOrchestrator.eventBus.emit('orientation:name-captured', { name });
    }
  }

  /**
   * Get fallback response when deviation handling fails.
   * Re-renders the current step's components (buttons, artifacts) so the user
   * sees the correct options even when the backend is unreachable.
   * @private
   */
  _getFallbackDeviationResponse() {
    // Get current step config — it already defines the correct components
    const step = this._getCurrentStep();
    const components = [];

    // Re-render artifacts if the step defines them
    if (step?.showQuestionnaireArtifact && step.questionnaireArtifactId) {
      this._loadQuestionnaireArtifact(step.questionnaireArtifactId, 0, { skipPostButtons: true });
    }
    if (step?.showInsight && this.currentInsight) {
      this._renderInlineInsight(this.currentInsight, 0);
    }

    // Collect buttons from step config (postArtifactButtons or buttons)
    const stepButtons = step?.postArtifactButtons || step?.buttons;
    if (stepButtons?.length) {
      components.push({
        type: 'buttons',
        buttons: stepButtons.map(btn => ({
          label: btn.label,
          action: `orientation:${btn.action}`,
          data: { action: btn.action },
          metadata: btn.metadata
        })),
        options: ORIENTATION_INPUT_BUTTON_OPTIONS
      });
    }

    // Last resort: generic continue
    if (!components.length) {
      components.push({
        type: 'buttons',
        buttons: DEVIATION_BUTTONS.default.map(btn => ({
          label: btn.label,
          action: `orientation:${btn.action}`,
          data: { action: btn.action }
        })),
        options: ORIENTATION_INPUT_BUTTON_OPTIONS
      });
    }

    return {
      type: 'instant',
      text: "I'd love to help with that. Let's finish getting set up first, and then we can explore together.",
      messageType: 'system',
      customClass: 'message--orientation',
      components
    };
  }

  // ========================================
  // HEADER CONTENT STRATEGY
  // ========================================

  getModeLabel() {
    return 'Orientation';
  }

  /**
   * Get header content strategy for dynamic header updates.
   *
   * Left block: "Orientation" title plus a percent for the chat progress
   * strip. Percent is completed work — (step−1)/total, so step 1 renders an
   * empty track and each phase advance fills a fifth. 100% appears only during
   * the completion beat (_emitCompletionBeat), as an exit animation — it is
   * never a resting state, because the beat is immediately followed by the
   * mode exit. The percent is withheld (null) until the header is visible: the
   * strip lives in .main, not the header, and would otherwise paint over the
   * name-capture screen whose header is hidden. That gate outranks the
   * completion override — a hidden header must not gain a full strip.
   *
   * Middle: phase title (or the terminal "Orientation complete" during the
   * beat) + "(Step N of 5)" badge.
   */
  getHeaderContentStrategy() {
    return {
      generateContent: async () => {
        const headerKey = this._getCurrentHeaderKey();
        let headerText = this._completing
          ? PHASE_HEADERS.COMPLETE
          : (PHASE_HEADERS[headerKey] || 'Getting started');

        if (headerText.includes('{name}')) {
          if (this.onboardingData.firstName) {
            headerText = interpolateContent(headerText, {
              name: this.onboardingData.firstName
            });
          } else {
            headerText = PHASE_HEADERS.INTRODUCTION_START;
          }
        }

        const currentStep = ORIENTATION_PROGRESS_STEPS[this.currentPhase] || 1;

        return {
          leftBlock: {
            title: this.getModeLabel(),
            percent: this._headerVisible
              ? (this._completing
                ? 100
                : ((currentStep - 1) / ORIENTATION_TOTAL_STEPS) * 100)
              : null
          },
          middleBlock: {
            html: `${headerText} <span class="orientation-header__step">(Step ${currentStep} of ${ORIENTATION_TOTAL_STEPS})</span>`
          }
        };
      },

      getUpdateTriggers: () => [
        'orientation:phase-changed',
        'orientation:step-changed',
        'orientation:name-captured',
        'orientation:ui-state',
        'orientation:completing',
        'questionnaire:completed',
        'orientation:insight-displayed'
      ],

      shouldUpdate: () => true
    };
  }

  // ========================================
  // PHASE STATE MACHINE
  // ========================================

  /**
   * Advance to the next step within current phase
   */
  async _advanceStep() {
    const phaseContent = ORIENTATION_CONTENT[this.currentPhase];
    if (!phaseContent?.steps) {
      // No more steps, advance phase
      return await this._advancePhase();
    }

    const nextStepIndex = this.currentStepIndex + 1;
    if (nextStepIndex >= phaseContent.steps.length) {
      // No more steps in this phase, advance to next phase
      return await this._advancePhase();
    }

    this.currentStepIndex = nextStepIndex;
    this._updatePlaceholder();

    // Emit step change for header update
    if (window.modeOrchestrator?.eventBus) {
      window.modeOrchestrator.eventBus.emit('orientation:step-changed', {
        phase: this.currentPhase,
        step: this.currentStepIndex
      });
    }

    return await this._renderCurrentStep();
  }

  /**
   * Advance to the next phase
   * Includes re-entrance guard to prevent race conditions from rapid button clicks
   */
  async _advancePhase() {
    // Re-entrance guard - prevent concurrent phase advances
    if (this._isAdvancingPhase) {
      return { type: 'instant', text: '' };
    }

    this._isAdvancingPhase = true;

    try {
      // Disable all prior interactive elements before advancing
      this._disableAllOrientationInteractives();
      this.deviationHistory = [];

      const nextPhase = getNextPhase(this.currentPhase);

      if (!nextPhase) {
        // Orientation complete
        return await this._completeOrientation();
      }

      // Persist phase transition
      await this._persistPhaseTransition(nextPhase);

      this.currentPhase = nextPhase;
      this.currentStepIndex = 0;
      this._updatePlaceholder();

      // Emit phase change for header update
      if (window.modeOrchestrator?.eventBus) {
        window.modeOrchestrator.eventBus.emit('orientation:phase-changed', {
          phase: this.currentPhase
        });
      }

      return await this._renderCurrentStep();
    } finally {
      this._isAdvancingPhase = false;
    }
  }

  /**
   * Render the current step's content
   */
  async _renderCurrentStep() {
    // Resumed into a just-converted account — acknowledge it instead of rendering
    // the normal (account-pitching) ACCOUNT_PROMPT step. Consumed per page load so
    // a mid-phase deviation → `continue_setup` re-render falls through to the
    // regular step's `authenticated` variant rather than repeating the beat.
    if (this._showAccountBeat && this.currentPhase === ORIENTATION_PHASES.ACCOUNT_PROMPT) {
      this._showAccountBeat = false;
      return await this._showAccountCreatedContent();
    }

    const phaseContent = ORIENTATION_CONTENT[this.currentPhase];
    if (!phaseContent?.steps) {
      console.warn('[OrientationMode] No content for phase:', this.currentPhase);
      return { type: 'instant', text: '' };
    }

    const step = phaseContent.steps[this.currentStepIndex];
    if (!step) {
      console.warn('[OrientationMode] No step at index:', this.currentStepIndex);
      return { type: 'instant', text: '' };
    }

    // Handle questionnaire step
    if (step.questionnaireId) {
      return await this._launchQuestionnaire(step.questionnaireId);
    }

    // Build message text (with interpolation)
    let messageText = step.message;
    if (step.messageVariants) {
      // Choose variant (priority order):
      // 1. Resuming without name → 'return_nameless' variant
      // 2. Resuming with name → 'return' variant
      // 3. Authenticated (step opts in with an 'authenticated' key) → that family,
      //    including its denied/skipped sub-variants
      // 4. Denied the insight → 'denied' variant
      // 5. Skipped ECR-12 questionnaire → 'skipped' variant
      // 6. No name provided → 'nameless' variant
      // 7. SSO users who skipped name step → 'sso' variant
      // 8. Otherwise, choose based on branch (account vs guest)
      const hasName = !!this.onboardingData.firstName;
      let variant;

      if (this._isResuming) {
        if (!hasName && step.messageVariants.return_nameless) {
          variant = 'return_nameless';
        } else if (step.messageVariants.return) {
          variant = 'return';
        }
      }

      if (!variant) {
        // Account status is resolved BEFORE denied/skipped, not after. Those two are
        // the guest-voiced copies — they pitch account creation — so letting them
        // match an authenticated user strands them on a "we'll need to create an
        // account" paragraph under the Continue-only `authenticatedButtons` set,
        // with no way to act on it. Ordering them first also made
        // `authenticated_denied`/`authenticated_skipped` unreachable, since the one
        // step carrying the authenticated family (ACCOUNT_PROMPT) defines the plain
        // `denied`/`skipped` keys too.
        //
        // Gating on `messageVariants.authenticated` keeps this scoped: ACCOUNT_PROMPT
        // is the only step that defines it, so every other step's resolution is
        // unchanged by the reorder.
        if (!window.app.isGuest && step.messageVariants.authenticated) {
          // Authenticated user (SSO or pre-existing account) — select sub-variant
          if (this.onboardingData.insightConfirmed === false && step.messageVariants.authenticated_denied) {
            variant = 'authenticated_denied';
          } else if (this.onboardingData.skippedLoveLanguages && step.messageVariants.authenticated_skipped) {
            variant = 'authenticated_skipped';
          } else {
            variant = 'authenticated';
          }
        } else if (this.onboardingData.insightConfirmed === false && step.messageVariants.denied) {
          variant = 'denied';
        } else if (this.onboardingData.skippedLoveLanguages && step.messageVariants.skipped) {
          // A guest who already declined the account ask needs the extra
          // disclosure line before a step that will gate on an account.
          variant = (this.onboardingData.branch === 'defer' && step.messageVariants.skipped_deferred)
            ? 'skipped_deferred'
            : 'skipped';
        } else if (!hasName && step.messageVariants.nameless) {
          variant = 'nameless';
        } else if (this._skippedNameStep && step.messageVariants.sso) {
          variant = 'sso';
        } else {
          variant = this.onboardingData.branch === 'account' ? 'account' : 'guest';
        }
      }

      messageText = step.messageVariants[variant] || step.messageVariants.default || step.messageVariants.guest;
    }

    // Clear resuming flag after first step render — must be outside messageVariants
    // block so it clears even for steps using plain `message` (e.g., DISCOVERY's
    // questionnaire_intro). Without this, _isResuming survives across phases and
    // the "return" variant incorrectly wins over "skipped" in INSIGHT phase.
    if (this._isResuming) {
      this._isResuming = false;
    }

    messageText = interpolateContent(messageText, {
      name: this.onboardingData.firstName
    });

    // Inject an illustrative memory-update card between paragraphs when the user
    // confirmed their insight and the step opts in via showUnderstanding. Nothing
    // is fetched — this demonstrates the feature, so the label names the demo
    // questionnaire ("How You Experience Love" — the orientation love-languages
    // assessment) and the wedge sweeps a fixed, dramatic 0→0.40 for everyone,
    // independent of their actual answers. Carrying NO <topic> id is what keeps
    // the card non-interactive (understandingCard.js omits the tap handler
    // without one), so this pre-auth use needs no demo gate.
    if (step.showUnderstanding && this.onboardingData.insightConfirmed === true) {
      const paragraphs = messageText.split('\n\n');
      if (paragraphs.length >= 2) {
        const understandingXml = '<topic-understanding>\n' +
          '<tier>1</tier><changed>true</changed>' +
          '<label>How You Experience Love</label><from>0</from><progress>0.40</progress>\n' +
          `<topics>${DEMO_UNDERSTANDING_TOPICS}</topics>\n` +
          '</topic-understanding>';
        paragraphs.splice(1, 0, understandingXml);
        messageText = paragraphs.join('\n\n');
      }
    }

    // Build response with components
    const response = {
      type: 'instant',
      text: messageText,
      messageType: 'system',
      customClass: 'message--orientation',
      components: [],
      // Include stagger delay if step has it (for sequential block rendering)
      ...(step.staggerDelay && { staggerDelay: step.staggerDelay })
    };

    // Add buttons if defined (supports single list or multiple groups)
    // Select button groups based on whether questionnaire was skipped
    const effectiveButtonGroups = (this.onboardingData.skippedLoveLanguages && step.skippedButtonGroups)
      ? step.skippedButtonGroups
      : step.buttonGroups;

    if (effectiveButtonGroups?.length) {
      for (const group of effectiveButtonGroups) {
        response.components.push({
          type: 'buttons',
          buttons: group.map(btn => ({
            label: btn.label,
            action: `orientation:${btn.action}`,
            data: { action: btn.action },
            metadata: btn.metadata
          })),
          options: ORIENTATION_INPUT_BUTTON_OPTIONS
        });
      }
    } else if (step.buttons?.length) {
      const isAuthenticated = !window.app.isGuest;
      const effectiveButtons = (isAuthenticated && step.authenticatedButtons) ? step.authenticatedButtons : step.buttons;
      response.components.push({
        type: 'buttons',
        buttons: effectiveButtons.map(btn => ({
          label: btn.label,
          action: `orientation:${btn.action}`,
          data: { action: btn.action },
          metadata: btn.metadata
        })),
        options: ORIENTATION_INPUT_BUTTON_OPTIONS
      });
    }

    // Handle insight display - render inline via <insight> structured block
    if (step.showInsight && this.currentInsight) {
      this._renderInlineInsight(this.currentInsight, step.componentDelay || 0);
    }

    // Handle questionnaire artifact display (inline artifact, not immediate launch)
    if (step.showQuestionnaireArtifact && step.questionnaireArtifactId) {
      // Load and display questionnaire artifact after text animation completes
      // componentDelay is hard-coded based on number of text blocks × staggerDelay + fadeInDuration
      this._loadQuestionnaireArtifact(step.questionnaireArtifactId, step.componentDelay || 0);
    }

    // Handle text input steps
    if (step.inputType === 'text') {
      // Input is handled by default chat input
      // Just update placeholder
      this._setInputPlaceholder(step.inputPlaceholder || OrientationMode.PLACEHOLDERS.DEFAULT);
    }

    // Trigger input attention after welcome step animation completes
    if (this.currentPhase === ORIENTATION_PHASES.INTRODUCTION &&
        this.currentStepIndex === 0 &&
        step.inputType === 'text' &&
        !this._welcomeAttentionTriggered) {
      this._welcomeAttentionTriggered = true;

      setTimeout(() => {
        if (window.modeOrchestrator?.eventBus) {
          window.modeOrchestrator.eventBus.emit('input:attention', { active: true });
          window.modeOrchestrator.eventBus.emit('input:focus');
        }
      }, 2000);
    }

    return response;
  }

  // ========================================
  // INPUT HANDLERS
  // ========================================

  /**
   * Handle name input from Phase 1
   * Uses Haiku LLM to validate and extract name, then routes to appropriate path
   */
  async _handleNameInput(userInput) {
    // Clear any previous validation state
    this.nameValidationState = null;
    this.pendingNameValue = null;

    // Show thinking indicator
    this._showThinkingIndicator('Thinking...');

    try {
      const { OrientationFlowClient } = await import('../../services/OrientationFlowClient.js');
      const flowClient = new OrientationFlowClient();
      const result = await flowClient.validateNameInput(userInput);

      this._hideThinkingIndicator();

      // Path 1: Valid name
      if (result.name && result.plausible) {
        return await this._saveNameAndAdvance(result.name);
      }

      // Path 2: Name extracted but not plausible - ask for confirmation
      if (result.name) {
        this.nameValidationState = 'confirming_name';
        this.pendingNameValue = result.name;
        return this._renderNameConfirmation(result.name);
      }

      // Path 3: No name extracted — route to deviation handler
      // Handles questions, exploration, name refusals with contextual LLM response
      return await this._handleDeviation(userInput);
    } catch (error) {
      console.error('[OrientationMode] Name validation error:', error);
      this._hideThinkingIndicator();
      // Fallback: ask for confirmation with raw input
      const name = userInput?.trim();
      if (name) {
        this.nameValidationState = 'confirming_name';
        this.pendingNameValue = name;
        return this._renderNameConfirmation(name);
      }
      return await this._handleDeviation(userInput);
    }
  }

  /**
   * Render confirmation prompt for extracted name that may not be plausible
   * @param {string} name - The extracted name to confirm
   * @returns {Object} Response object with confirmation UI
   * @private
   */
  _renderNameConfirmation(name) {
    return {
      type: 'instant',
      text: `It looks like you're just exploring — no problem!\n\nIf you'd like, share your name below. It helps me personalize your experience, but it's completely optional.`,
      messageType: 'system',
      customClass: 'message--orientation',
      components: [{
        type: 'buttons',
        buttons: [
          {
            label: `Skip for now`,
            action: 'orientation:decline_name'
          },
        ],
        options: ORIENTATION_INPUT_BUTTON_OPTIONS
      }]
    };
  }

  /**
   * Save the validated name and advance to next step
   * @param {string} rawName - The validated name to save (normalized here)
   * @returns {Promise<Object>} Response from advancing step
   * @private
   */
  async _saveNameAndAdvance(rawName) {
    // Normalize before anything reads it, so the header, the greeting, and the
    // persisted row all show the same casing.
    const name = normalizeFirstName(rawName);
    this.onboardingData.firstName = name;

    // Persist name via API
    try {
      const { API } = await import('../../core/api.js');
      await API.captureGuestName(name);
    } catch (error) {
      console.error('[OrientationMode] Failed to persist name:', error);
      // Continue anyway - name is stored in mode state
    }

    // Emit name captured event for header update
    if (window.modeOrchestrator?.eventBus) {
      window.modeOrchestrator.eventBus.emit('orientation:name-captured', { name });
    }

    // Clear validation state
    this.nameValidationState = null;
    this.pendingNameValue = null;

    // Show header now that name step is complete
    this._emitUIState('chat');

    // Advance to next step
    return await this._advanceStep();
  }

  /**
   * Advance without saving a name (user declined to provide one)
   * Skips the name capture API call since it requires a name value.
   * @returns {Promise<Object>} Response from advancing step
   * @private
   */
  async _advanceWithoutName() {
    // Keep firstName as null (don't save anything)
    this.onboardingData.firstName = null;

    // Emit name captured event with null for header update
    // Header logic will use non-personalized variant
    if (window.modeOrchestrator?.eventBus) {
      window.modeOrchestrator.eventBus.emit('orientation:name-captured', { name: null });
    }

    // Clear validation state
    this.nameValidationState = null;
    this.pendingNameValue = null;

    // Show header now that name step is complete
    this._emitUIState('chat');

    // Advance to next step
    return await this._advanceStep();
  }

  // ========================================
  // BUTTON HANDLERS
  // ========================================

  /**
   * Handle button action from orientation flow
   */
  async _handleButtonAction(action) {
    console.log('[OrientationMode] Button action:', action);

    // Layer 2 stale button protection — reject actions not valid for current phase
    if (!this._isActionValidForCurrentPhase(action)) {
      console.warn('[OrientationMode] Stale action ignored:', action, 'current phase:', this.currentPhase);
      return { type: 'instant', text: '' };
    }

    switch (action) {
      case 'continue': {
        const currentStep = this._getCurrentStep();
        // If step has advanceOnPanelClose, close panel and let handler advance
        if (currentStep?.advanceOnPanelClose) {
          if (window.modeOrchestrator?.eventBus) {
            window.modeOrchestrator.eventBus.emit('right-panel:close');
          }
          return { type: 'instant', text: '' };
        }
        return await this._advanceStep();
      }

      case 'start_questionnaire':
        // Next step should be a questionnaire
        return await this._advanceStep();

      case 'begin_questionnaire': {
        // Open the affection-patterns questionnaire panel — identical to clicking the
        // artifact card. The launcher was stashed by app.handleOrientationQuestionnaire.
        // The Begin/Skip input group is torn down by the central click teardown; it is
        // restored on panel close (without completion) by the panelClosed handler.
        const launcher = window.app?.modules?.chatManager?.currentActiveQuestionnaire?.launcher;
        launcher?._handleClick?.();
        return { type: 'instant', text: '' };
      }

      case 'create_account':
        this.onboardingData.branch = 'account';
        return await this._handleAccountCreation();

      case 'defer_account':
        this.onboardingData.branch = 'defer';
        await this._persistStepData({ branch: 'defer' });
        // Show guest continues content
        return await this._showGuestContinuesContent();

      case 'confirm_insight':
        this.onboardingData.insightConfirmed = true;
        await this._persistStepData({ insightConfirmed: true });
        return await this._advanceStep();

      case 'deny_insight':
        this.onboardingData.insightConfirmed = false;
        await this._persistStepData({ insightConfirmed: false });
        return await this._advanceStep();

      case 'discuss_insight':
        return await this._enterInsightDiscussion();

      case 'continue_setup':
        this.subState = null;
        this.deviationHistory = []; // Clear deviation history
        return await this._renderCurrentStep();

      case 'continue_questionnaire':
      case 'pause_questionnaire':
        // No-op: these actions are no longer reachable since chat input is disabled
        // during questionnaires. Kept as explicit no-ops to prevent default case fallthrough.
        return { type: 'instant', text: '' };

      case 'handle_urgent':
        return await this._handleUrgentRequest();

      case 'return_to_orientation':
        this.subState = null;
        return await this._renderCurrentStep();

      case 'complete_foundation':
        // Skipped-questionnaire path: there is no demo session to convert at the
        // close of, so the ask lands here instead — this click is the moment the
        // guest commits to a journey worth keeping.
        if (await this._maybeGateOnAccount('complete_foundation', 'transition_foundation')) {
          return { type: 'instant', text: '' };
        }
        return await this._transitionToFoundation();

      case 'scenario_love_language':
        return await this._startOrientationAdviceSession();

      case 'explore_own':
        return await this._completeOrientation();

      case 'dismiss':
        // Inline "Exit" button — delegate to OrientationCloseManager, which
        // owns the single source of truth for the dismissal action (also
        // invoked by the header close button click).
        await window.app?.modules?.orientationCloseManager?.dismiss();
        return { type: 'instant', text: '' };

      case 'account_created':
        // Account was created - show account setup content
        return await this._showAccountCreatedContent();

      case 'confirm_name':
        // User confirmed the extracted name
        return await this._saveNameAndAdvance(this.pendingNameValue);

      case 'decline_name':
        // User declined to provide a name - continue without saving a name
        return await this._advanceWithoutName();

      case 'skip_love_languages': {
        // Set skip flag and persist
        this.onboardingData.skippedLoveLanguages = true;
        await this._persistStepData({ skippedLoveLanguages: true });

        // Disable stale interactives and clear deviation history
        // (this handler bypasses _advancePhase, so we must do it manually)
        this._disableAllOrientationInteractives();
        this.deviationHistory = [];

        // Set a generic placeholder insight for display when questionnaire is skipped
        this.currentInsight = {
          id: 'generic-love-languages',
          title: 'Love Languages - Example',
          insightType: 'individual',
          insightCode: 'affection_patterns',
          isGeneric: true,
          displayLabel: 'How You Experience Love (Example)',
          displayItems: [
            { header: 'How you give love', subtitle: 'Everyone has a primary way they express affection — through words, actions, gifts, quality time, or physical touch.' },
            { header: 'How you receive love', subtitle: 'Your preference for receiving love may differ from how you give it. Understanding both patterns strengthens connection.' },
            { header: 'Bridging differences', subtitle: 'When partners have different affection patterns, knowing each other\'s preferences helps bridge communication gaps.' }
          ],
          content: {
            type: 'clinical_assessment',
            content: 'Love languages describe the ways people prefer to give and receive affection. Understanding your love language — and your partner\'s — can help bridge communication gaps and deepen emotional connection.'
          }
        };

        // Mark questionnaire as no longer active
        this.questionnaireActive = false;
        this._updatePlaceholder();

        // Advance to INSIGHT phase, step 1 (insight_display)
        // Skip the 'processing' step since we're not actually processing anything
        this.currentPhase = ORIENTATION_PHASES.INSIGHT;
        this.currentStepIndex = 1; // Go directly to insight_display step

        // Persist phase transition
        await this._persistStepData({
          skippedLoveLanguages: true
        }, PHASE_TO_STEP[ORIENTATION_PHASES.INSIGHT]);

        // Emit phase change for header update
        if (window.modeOrchestrator?.eventBus) {
          window.modeOrchestrator.eventBus.emit('orientation:phase-changed', {
            phase: this.currentPhase
          });
        }

        return await this._renderCurrentStep();
      }

      default:
        console.warn('[OrientationMode] Unknown action:', action);
        return await this._advanceStep();
    }
  }

  // ========================================
  // QUESTIONNAIRE HANDLING
  // ========================================

  /**
   * Launch a questionnaire
   */
  async _launchQuestionnaire(questionnaireId) {
    console.log('[OrientationMode] Launching questionnaire:', questionnaireId);

    this.questionnaireActive = true;
    this._showThinkingIndicator('Loading questionnaire...');

    // Import and use the orientation flow client
    try {
      const { OrientationFlowClient } = await import('../../services/OrientationFlowClient.js');
      const flowClient = new OrientationFlowClient();

      // Load questionnaire via backend
      const result = await flowClient.loadQuestionnaire(questionnaireId);

      this._hideThinkingIndicator();

      if (result.questionnaire) {
        // Emit event to open questionnaire panel
        if (window.modeOrchestrator?.eventBus) {
          window.modeOrchestrator.eventBus.emit('orientation:questionnaire-ready', {
            questionnaire: result.questionnaire,
            pathMetadata: result.pathMetadata
          });
        }

        return {
          type: 'instant',
          text: '',
          messageType: 'system'
        };
      }
    } catch (error) {
      console.error('[OrientationMode] Failed to launch questionnaire:', error);
      this._hideThinkingIndicator();

      // Graceful degradation - skip questionnaire
      return {
        type: 'instant',
        text: "We're having trouble loading this questionnaire. Let's continue with the rest of the orientation.",
        messageType: 'error',
        components: [{
          type: 'buttons',
          buttons: [{ label: 'Continue', action: 'orientation:continue' }],
          options: ORIENTATION_INPUT_BUTTON_OPTIONS
        }]
      };
    }
  }

  /**
   * Emit insight artifact event after optional delay.
   * Delay allows text animation to complete before artifact appears.
   * @param {Object} insight - The insight object to display
   * @param {number} componentDelay - Delay in ms before emitting artifact
   * @private
   */
  /**
   * Render insight inline using <insight> structured block tag.
   * Builds XML from displayItems and renders through displayAssistantMessage pipeline.
   * Falls back to _emitInsightArtifact if no displayItems available.
   *
   * @param {Object} insight - Insight object with displayLabel and displayItems
   * @param {number} [componentDelay=0] - Delay before rendering (ms)
   * @private
   */
  async _renderInlineInsight(insight, componentDelay = 0) {
    if (componentDelay > 0) {
      await this._delay(componentDelay);
    }

    // Resolve detail content: compose on demand from scoreResult, or use existing
    const detailContent = await this._getInsightDetailContent(insight);

    // Build <insight> XML from display items + score data
    const label = insight.displayLabel || 'Insight';
    const items = (insight.displayItems || []).slice(0, 3);
    const itemsXml = items
      .map(item => `<item><header>${item.header}</header><subtitle>${item.subtitle}</subtitle></item>`)
      .join('\n');
    const detailXml = detailContent ? `<detail>${detailContent}</detail>` : '';

    // Primary/secondary pattern names from scoreResult (rendered as pill tags)
    let titleXml = '';
    let subtitleXml = '';
    if (insight.scoreResult) {
      const { primaryPattern, secondaryPattern, isBalancedProfile } = insight.scoreResult;
      if (isBalancedProfile) {
        titleXml = '<title>Balanced Profile</title>';
      } else if (primaryPattern) {
        titleXml = `<title>${primaryPattern.displayName}</title>`;
        if (secondaryPattern && secondaryPattern.score >= 7) {
          subtitleXml = `<secondary>${secondaryPattern.displayName}</secondary>`;
        }
      }
    }

    const insightXml = `<insight>\n<label>${label}</label>\n${titleXml}\n${subtitleXml}\n${itemsXml}\n${detailXml}\n</insight>`;

    // Optional trailing paragraph rendered inline directly beneath the card, in the
    // same bubble (BlockStreamBuffer splits prose after the closing </insight> tag).
    // The skip path swaps in its own trailing message + buttons (see the
    // `skipped*` keys on the insight_display step) because the card it renders is a
    // generic example, not a reading of this user.
    const currentStep = this._getCurrentStep();
    const skipped = !!this.onboardingData.skippedLoveLanguages;
    const trailingMessage = skipped && currentStep?.skippedInsightTrailingMessage !== undefined
      ? currentStep.skippedInsightTrailingMessage
      : currentStep?.insightTrailingMessage;
    const trailingText = trailingMessage
      ? interpolateContent(trailingMessage, { name: this.onboardingData.firstName })
      : '';
    const messageBody = trailingText ? `${insightXml}\n\n${trailingText}` : insightXml;

    // Render through the standard message pipeline (BlockStreamBuffer detects the XML tag)
    const chatManager = window.modeOrchestrator?.chatManager || window.app?.modules?.chatManager;
    if (chatManager) {
      chatManager.displayAssistantMessage(messageBody, {
        messageType: 'system',
        customClass: 'message--orientation'
      });
    }

    // Render post-insight buttons
    const postButtons = (skipped && currentStep?.skippedPostArtifactButtons?.length)
      ? currentStep.skippedPostArtifactButtons
      : currentStep?.postArtifactButtons;
    if (postButtons?.length) {
      setTimeout(() => {
        this._renderPostArtifactButtons(postButtons);
      }, 500);
    }
  }

  async _emitInsightArtifact(insight, componentDelay = 0) {
    // Wait for text animation to complete before showing artifact
    if (componentDelay > 0) {
      await new Promise(resolve => setTimeout(resolve, componentDelay));
    }

    if (window.modeOrchestrator?.eventBus) {
      window.modeOrchestrator.eventBus.emit('component:artifact-requested', {
        type: 'insight',
        data: {
          status: 'ready',
          title: insight.title || 'Your insight',
          insightId: insight.id,
          content: insight.content,
          insightType: insight.insightType || 'individual',
          insightCode: insight.insightCode,
          pathId: 'orientation',
          isGeneric: insight.isGeneric || false
        }
      });
    }

    // Render post-artifact buttons if defined in step (same pattern as questionnaire artifacts)
    const currentStep = this._getCurrentStep();
    if (currentStep?.postArtifactButtons?.length) {
      setTimeout(() => {
        this._renderPostArtifactButtons(currentStep.postArtifactButtons);
      }, 500);
    }
  }

  /**
   * Load and display a questionnaire artifact inline (after message renders).
   * Unlike _launchQuestionnaire, this shows the artifact card without immediately
   * opening the questionnaire panel - user clicks "Begin" to start.
   */
  async _loadQuestionnaireArtifact(questionnaireId, componentDelay = 0, { skipPostButtons = false } = {}) {
    console.log('[OrientationMode] Loading questionnaire artifact:', questionnaireId, 'delay:', componentDelay);

    // Wait for text animation to complete before showing artifact
    if (componentDelay > 0) {
      await new Promise(resolve => setTimeout(resolve, componentDelay));
    }

    try {
      const { OrientationFlowClient } = await import('../../services/OrientationFlowClient.js');
      const flowClient = new OrientationFlowClient();

      // Load questionnaire data
      const result = await flowClient.loadQuestionnaire(questionnaireId);

      if (result.questionnaire) {
        // Mark questionnaire as active (user will interact with artifact)
        this.questionnaireActive = true;

        // Emit event to render questionnaire artifact in chat
        if (window.modeOrchestrator?.eventBus) {
          window.modeOrchestrator.eventBus.emit('orientation:questionnaire-ready', {
            questionnaire: result.questionnaire,
            pathMetadata: result.pathMetadata
          });
        }

        // Render post-artifact buttons if defined in step (e.g., skip questionnaire)
        // Skip when called from deviation path — STAGE_COMPONENTS provides its own buttons
        if (!skipPostButtons) {
          const currentStep = this._getCurrentStep();
          if (currentStep?.postArtifactButtons?.length) {
            setTimeout(() => {
              this._renderPostArtifactButtons(
                currentStep.postArtifactButtons,
                ORIENTATION_QUESTIONNAIRE_BUTTON_OPTIONS
              );
            }, 500);
          }
        }
      }
    } catch (error) {
      console.error('[OrientationMode] Failed to load questionnaire artifact:', error);
      // On error, show a fallback error message in chat
      const chatManager = window.app?.modules?.chatManager;
      if (chatManager) {
        chatManager.displayAssistantMessage(
          "We're having trouble loading this questionnaire. Let's try again.",
          { animated: false, messageType: 'error' }
        );
      }
    }
  }

  /**
   * Render buttons after an artifact (e.g., skip questionnaire)
   * @param {Array} buttons - Button configurations from step definition
   * @private
   */
  async _renderPostArtifactButtons(buttons, options = ORIENTATION_INPUT_BUTTON_OPTIONS) {
    if (!buttons?.length || !window.modeOrchestrator?.renderComponents) return;

    const components = [{
      type: 'buttons',
      buttons: buttons.map(btn => ({
        label: btn.label,
        action: `orientation:${btn.action}`,
        data: { action: btn.action },
        metadata: btn.metadata
      })),
      options
    }];

    await window.modeOrchestrator.renderComponents(components, {});
  }

  /**
   * Handle questionnaire completion
   * For insight-generating questionnaires (ECR-12, Affection Patterns), this displays
   * a two-step flow: processing message → pause → insight display
   */
  async _handleQuestionnaireComplete(data) {
    console.log('[OrientationMode] Questionnaire completed:', data);

    this.questionnaireActive = false;
    this._updatePlaceholder();

    const { questionnaireId, insightId, narrativeContent, narrativeDisplayLabel, narrativeDisplayItems, narrativeDetailContent, classificationCode, scoreResult } = data;

    // Slide the panel out and float the completed artifact to the top as one motion.
    //
    // No Begin/Skip teardown needed here — the `questionnaire:opened` handler in
    // ModeOrchestrator tore the group down when the panel opened, and this method
    // clears `questionnaireActive` above, so the panel-close restore stays inert.
    const chatManager = window.app?.modules?.chatManager;
    if (chatManager && questionnaireId) {
      await chatManager.settleArtifact(questionnaireId);
    }

    // Store insight ID if generated (Love Languages questionnaire)
    if (questionnaireId === QUESTIONNAIRE_IDS.LOVE_LANGUAGES && insightId) {
      this.onboardingData.loveLangsInsightId = insightId;
      await this._persistStepData({ loveLangsInsightId: insightId });
    }

    // Build insight from submit response data (no separate API fetch needed)
    if (insightId && narrativeContent) {
      this.currentInsight = {
        id: insightId,
        title: 'Love Languages',
        insightType: 'individual',
        insightCode: 'affection_patterns',
        displayLabel: narrativeDisplayLabel || 'How You Experience Love',
        displayItems: narrativeDisplayItems || null,
        detailContent: narrativeDetailContent || null,
        classificationCode: classificationCode || null,
        scoreResult: scoreResult || null,
        content: {
          type: 'clinical_assessment',
          content: narrativeContent
        }
      };
      this.insightDisplayed = true;

      // Persist display items, narrative content, and scoring data for resumption.
      // scoreResult + classificationCode enable on-demand detail composition,
      // so content stays current when templates are updated.
      await this._persistStepData({
        loveLangsNarrativeContent: narrativeContent,
        loveLangsDisplayLabel: narrativeDisplayLabel,
        loveLangsDisplayItems: narrativeDisplayItems,
        loveLangsClassificationCode: classificationCode,
        loveLangsScoreResult: scoreResult
      });

      if (window.modeOrchestrator?.eventBus) {
        window.modeOrchestrator.eventBus.emit('orientation:insight-displayed', { insightId });
      }
    }

    // Advance to next phase/step
    await this._advanceToNextPhaseOrStep();

    // Get current step config to check for auto-advance
    const phaseContent = ORIENTATION_CONTENT[this.currentPhase];
    const currentStep = phaseContent?.steps?.[this.currentStepIndex];

    // Display step 0 (processing message)
    const step0Response = await this._renderCurrentStep();
    const animationDuration = await this._displayOrientationResponse(step0Response);

    // If step has autoAdvanceDelay, show thinking indicator during the pause.
    // Must activate AFTER _displayOrientationResponse because MessageRenderer.finish()
    // unconditionally deactivates any active indicator.
    if (currentStep?.autoAdvanceDelay) {
      // Wait for text fade-in to complete, then 200ms pause before indicator
      await this._delay(animationDuration + 200);
      this._showThinkingIndicator();
      await this._delay(currentStep.autoAdvanceDelay);

      // Hide indicator before showing insight
      this._hideThinkingIndicator();

      // Advance to step 1 (insight display)
      this.currentStepIndex++;
      this._updatePlaceholder();

      // Emit step change for header update
      if (window.modeOrchestrator?.eventBus) {
        window.modeOrchestrator.eventBus.emit('orientation:step-changed', {
          phase: this.currentPhase,
          step: this.currentStepIndex
        });
      }

      // Display step 1 (insight + button)
      const step1Response = await this._renderCurrentStep();
      await this._displayOrientationResponse(step1Response);
    }
  }

  /**
   * Advance to next phase or step (helper for questionnaire completion)
   * Updates state without rendering - rendering is handled separately
   * @private
   */
  async _advanceToNextPhaseOrStep() {
    const phaseContent = ORIENTATION_CONTENT[this.currentPhase];
    const nextStepIndex = this.currentStepIndex + 1;

    if (!phaseContent?.steps || nextStepIndex >= phaseContent.steps.length) {
      // Advance to next phase
      const nextPhase = getNextPhase(this.currentPhase);
      if (nextPhase) {
        await this._persistPhaseTransition(nextPhase);
        this.currentPhase = nextPhase;
        this.currentStepIndex = 0;
        this._updatePlaceholder();

        if (window.modeOrchestrator?.eventBus) {
          window.modeOrchestrator.eventBus.emit('orientation:phase-changed', {
            phase: this.currentPhase
          });
        }
      }
    } else {
      // Advance within current phase
      this.currentStepIndex = nextStepIndex;
      this._updatePlaceholder();

      if (window.modeOrchestrator?.eventBus) {
        window.modeOrchestrator.eventBus.emit('orientation:step-changed', {
          phase: this.currentPhase,
          step: this.currentStepIndex
        });
      }
    }
  }

  /**
   * Display an orientation response directly via ChatManager
   * Used when displaying messages outside the normal orchestrator flow
   * @param {Object} response - Response object from _renderCurrentStep
   * @param {Object} [options]
   * @param {boolean} [options.scroll=false] - Pin this message to the top of the
   *   reading frame. Only the reading frame ever scrolls, and it is normally set by
   *   pinning the user's bubble (MessageList.addUserMessage); assistant content and
   *   interactive components never scroll on their own. Callers reached from an event
   *   rather than a user turn (no bubble exists to pin) opt in here so their content
   *   doesn't land below the fold.
   * @returns {number} Animation duration in ms
   * @private
   */
  async _displayOrientationResponse(response, { scroll = false } = {}) {
    if (!response?.text) return 0;

    const chatManager = window.modeOrchestrator?.chatManager || window.app?.modules?.chatManager;
    if (!chatManager) {
      console.error('[OrientationMode] ChatManager not available');
      return 0;
    }

    // Display the text message
    const result = chatManager.displayAssistantMessage(response.text, {
      messageType: response.messageType,
      staggerDelay: response.staggerDelay,
      customClass: response.customClass
    });

    // Pin before the text animates rather than after, so the frame is already in
    // place as blocks fade in (a late scroll would animate the text off-screen and
    // then jump).
    if (scroll && result?.element) {
      chatManager.messageList?.scrollToMessage?.(result.element);
    }

    const animationDuration = result?.animationDuration || 0;

    // Wait for text animation to complete before showing components
    if (animationDuration > 0 && response.components?.length) {
      await this._delay(animationDuration);
    }

    // Render components (buttons, etc.)
    if (response.components?.length && window.modeOrchestrator?.renderComponents) {
      await window.modeOrchestrator.renderComponents(response.components, {});
    }

    return animationDuration;
  }

  /**
   * Promise-based delay helper
   * @param {number} ms - Milliseconds to wait
   * @private
   */
  _delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ========================================
  // INSIGHT HANDLING
  // ========================================

  /**
   * Rebuild insight from persisted onboarding data (used on resume)
   * @private
   */
  _rebuildInsightFromPersistedData() {
    const {
      loveLangsInsightId, loveLangsNarrativeContent, loveLangsDisplayLabel,
      loveLangsDisplayItems, loveLangsDetailContent,
      loveLangsClassificationCode, loveLangsScoreResult
    } = this.onboardingData;

    if (loveLangsInsightId && loveLangsNarrativeContent) {
      this.currentInsight = {
        id: loveLangsInsightId,
        title: 'Love Languages',
        insightType: 'individual',
        insightCode: 'affection_patterns',
        displayLabel: loveLangsDisplayLabel || 'How You Experience Love',
        displayItems: loveLangsDisplayItems || null,
        // detailContent composed on demand when panel opens (see _getInsightDetailContent)
        detailContent: null,
        classificationCode: loveLangsClassificationCode || null,
        scoreResult: loveLangsScoreResult || null,
        // Legacy fallback: use persisted detailContent if no scoreResult available
        _legacyDetailContent: loveLangsDetailContent || null,
        content: {
          type: 'clinical_assessment',
          content: loveLangsNarrativeContent
        }
      };
      this.insightDisplayed = true;

      if (window.modeOrchestrator?.eventBus) {
        window.modeOrchestrator.eventBus.emit('orientation:insight-displayed', {
          insightId: loveLangsInsightId
        });
      }
    }
  }

  /**
   * Resolve detail content for an insight, composing on demand when possible.
   * With scoreResult + classificationCode available, fetches freshly composed
   * content from the backend — the only producer that stamps the framework
   * identity (title-block) and answered count the shared detail panel reads,
   * so it wins even when the submit response already carried a detail string.
   * That string, then legacy persisted data, are the fallbacks if the fetch
   * fails.
   *
   * @param {Object} insight - Insight object
   * @returns {Promise<string|null>} Detail content markdown
   * @private
   */
  async _getInsightDetailContent(insight) {
    // Compose on demand from scoreResult (fresh submit or persisted)
    if (insight.scoreResult && insight.classificationCode) {
      try {
        const { API_URL } = await import('../../core/api.js');
        const response = await fetch(`${API_URL}/orientation-flow/compose-insight-detail`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            classificationCode: insight.classificationCode,
            scoreResult: insight.scoreResult
          })
        });
        const result = await response.json();
        if (result.success && result.data?.detailContent) {
          return result.data.detailContent;
        }
      } catch (error) {
        console.warn('[OrientationMode] Failed to compose insight detail, falling back:', error.message);
      }
    }

    // Fallbacks: the submit response's unstamped detail string, then persisted
    // static detailContent from before the compose refactor.
    return insight.detailContent || insight._legacyDetailContent || null;
  }

  /**
   * Enter insight discussion sub-state
   */
  async _enterInsightDiscussion() {
    this.subState = ORIENTATION_SUB_STATES.DISCUSSING_INSIGHT;
    this.discussionExchanges = 0;

    return {
      type: 'instant',
      text: "What stands out to you about this insight? I'm happy to explore it briefly.",
      messageType: 'system'
    };
  }

  // ========================================
  // ACCOUNT HANDLING
  // ========================================

  /**
   * Open the conversion overlay in place of a guest-only action, carrying the
   * intent so the click continues once the account exists and the TRANSITION
   * controls come back if it is declined.
   *
   * @param {string} action - Action name; must have a continuation handler registered
   * @param {string} context - Overlay copy context
   * @returns {Promise<boolean>} True if the gate opened (caller must not proceed)
   * @private
   */
  async _maybeGateOnAccount(action, context) {
    if (window.app?.isGuest !== true) return false;

    await window.app.openConversionGate({
      action,
      context,
      groupId: null,
      actionData: {},
      // Orientation renders its own controls rather than a registered button
      // group, so re-arming by groupId cannot restore them — re-render the step.
      restore: async () => {
        const response = await this._renderCurrentStep();
        await this._displayOrientationResponse(response, { scroll: true });
      }
    });
    return true;
  }

  /**
   * Handle account creation request
   */
  async _handleAccountCreation() {
    // Emit event to show login overlay in account creation mode
    if (window.modeOrchestrator?.eventBus) {
      window.modeOrchestrator.eventBus.emit('orientation:show-account-creation');
    }

    // Return empty response - account creation UI handles the flow
    return {
      type: 'instant',
      text: '',
      messageType: 'system'
    };
  }

  /**
   * Show guest continues content (deferred account creation)
   */
  async _showGuestContinuesContent() {
    const content = ORIENTATION_CONTENT.GUEST_CONTINUES;
    const step = content.steps[0];

    return {
      type: 'instant',
      text: step.message,
      messageType: 'system',
      customClass: 'message--orientation',
      components: [{
        type: 'buttons',
        buttons: step.buttons.map(btn => ({
          label: btn.label,
          action: `orientation:${btn.action}`,
          data: { action: btn.action },
          metadata: btn.metadata
        })),
        options: ORIENTATION_INPUT_BUTTON_OPTIONS
      }]
    };
  }

  /**
   * Show account created content (successful account creation)
   * Called after successful guest-to-account conversion
   */
  async _showAccountCreatedContent() {
    const content = ORIENTATION_CONTENT.ACCOUNT_SETUP;
    const step = content.steps[0];

    // Select appropriate variant based on whether user has a name
    let messageText;
    if (step.messageVariants) {
      const hasName = !!this.onboardingData.firstName;
      const variant = hasName ? 'default' : 'nameless';
      messageText = step.messageVariants[variant] || step.messageVariants.default;
    } else {
      messageText = step.message;
    }

    // Interpolate in case variant still contains {name}
    messageText = interpolateContent(messageText, {
      name: this.onboardingData.firstName
    });

    // Let Continue button advance naturally via _advanceStep() → phase boundary → _advancePhase()
    // (Previously used setTimeout(_advancePhase) which silently skipped TRANSITION content)

    return {
      type: 'instant',
      text: messageText,
      messageType: 'system',
      customClass: 'message--orientation',
      components: [{
        type: 'buttons',
        buttons: step.buttons.map(btn => ({
          label: btn.label,
          action: `orientation:${btn.action}`,
          data: { action: btn.action },
          metadata: btn.metadata
        })),
        options: ORIENTATION_INPUT_BUTTON_OPTIONS
      }]
    };
  }

  // ========================================
  // COMPLETION
  // ========================================

  /**
   * Fill the chat progress strip to 100% and swap the header to its terminal
   * copy — the visual "you finished orientation" beat that precedes every exit
   * from the mode.
   *
   * Synchronous by design: callers fire this first, start their completion
   * network call immediately after, then await _holdCompletionBeat() alongside
   * it, so the animation costs no wall-clock the user was not already
   * spending. The strategy reached here is still orientation's own —
   * ModeOrchestrator does not activate the next mode's strategy until well
   * after switchMode's onExit — so the emit lands in the right generateContent.
   *
   * @private
   */
  _emitCompletionBeat() {
    if (this._completing) return;
    this._completing = true;
    window.modeOrchestrator?.eventBus?.emit('orientation:completing', {
      phase: this.currentPhase
    });
  }

  /**
   * Hold long enough for the strip's width transition to land and read as a
   * finished state. No-op when the header — and therefore the strip — is
   * hidden, so an exit from a headerless screen is not padded for an animation
   * nobody can see.
   *
   * @private
   */
  async _holdCompletionBeat() {
    if (!this._headerVisible) return;
    await this._delay(ORIENTATION_COMPLETE_BEAT_MS);
  }

  /**
   * Mark onboarding complete on the backend and mirror the terminal step into
   * the in-memory user. The mirror is load-bearing: the router's Priority 0
   * gate reads onboardingStep from that object, so without it, exiting the
   * advice session lands on a blank chat because isOrientationInProgress()
   * still returns true.
   *
   * Never rejects — a failed completion call must not strand the user inside
   * orientation, and callers hold this promise unawaited while the completion
   * beat animates.
   *
   * @private
   * @returns {Promise<void>}
   */
  async _markOnboardingComplete() {
    try {
      const { API } = await import('../../core/api.js');
      await API.completeGuestOnboarding();
      this._mirrorPersistedState('complete');
    } catch (error) {
      console.error('[OrientationMode] Failed to mark orientation complete:', error);
    }
  }

  /**
   * Start the orientation advice session — guided love language conversation.
   * Completes onboarding, switches to ADVICE mode with orientation context,
   * and sends a silent message to trigger the AI's opening response.
   * @private
   */
  async _startOrientationAdviceSession() {
    await this._finalizeOrientation();

    // Switch to advice mode with orientation context
    if (window.modeOrchestrator) {
      await window.modeOrchestrator.switchMode('ADVICE', {
        entryType: 'orientation_advice',
        loveLangsScoreResult: this.onboardingData.loveLangsScoreResult || null,
        insightConfirmed: this.onboardingData.insightConfirmed ?? null,
        skipWelcome: true
      });

      // Send synthetic silent message to trigger AI's opening response
      // Must be non-empty — empty strings are not stored as user messages
      await window.modeOrchestrator.handleUserMessage('Discuss how I experience love', {
        metadata: { source: 'orientation_scenario', silent: true }
      });
    }

    return { type: 'instant', text: '' };
  }

  /**
   * Launch the Foundation path ("Starting Your Journey") directly from
   * orientation — the primary next step offered at TRANSITION when the user
   * skipped the questionnaire and the advice-session demo is therefore
   * unavailable.
   *
   * Mirrors ActionRouter's `orientation_to_foundation`, the twin route out of
   * the post-orientation advice session, so both terminuses land identically:
   * `skipWelcome` suppresses Discovery's generic greeting, and
   * `_enterDiscoveryNavigation` puts the path's navigation in place. Notably it
   * does NOT route through _completeOrientation — that returns the user to the
   * dashboard, which would bounce them through it on the way to Foundation.
   */
  async _transitionToFoundation() {
    await this._finalizeOrientation();

    if (window.modeOrchestrator) {
      await window.modeOrchestrator.switchMode('DISCOVERY', {
        pathId: 'foundation',
        fromOrientation: true,
        skipWelcome: true
      });
      try {
        await window.modeOrchestrator._enterDiscoveryNavigation('foundation');
      } catch (error) {
        console.error('[OrientationMode] Failed to enter Foundation navigation:', error);
        window.modeOrchestrator.chatManager?.displayError?.('Failed to load Starting Your Journey. Please try again.');
      }
    }

    return { type: 'instant', text: '' };
  }

  /**
   * Run the completion beat, mark orientation complete on the backend, and
   * announce it — the three steps every route out of orientation shares. Where
   * the user lands afterwards is the caller's business: the dashboard
   * (_completeOrientation), the orientation advice session
   * (_startOrientationAdviceSession), or the Foundation path
   * (_transitionToFoundation).
   *
   * The beat is fired before the network call and awaited alongside it, so the
   * strip's fill to 100% costs no wall-clock the user was not already spending.
   * @private
   */
  async _finalizeOrientation() {
    this._emitCompletionBeat();
    const completion = this._markOnboardingComplete();
    await this._holdCompletionBeat();
    await completion;

    // Triggers FeatureToggleService.configureForNormalMode
    if (window.modeOrchestrator?.eventBus) {
      window.modeOrchestrator.eventBus.emit('orientation:completed', {
        data: this.onboardingData
      });
    }
  }

  /**
   * Complete the orientation flow and return to the dashboard
   */
  async _completeOrientation() {
    console.log('[OrientationMode] Completing orientation');

    await this._finalizeOrientation();

    // Return to dashboard
    if (window.modeOrchestrator) {
      window.modeOrchestrator.returnToDashboard();
    }
    window.app?.modules?.pageManager?.showDashboard?.();
    window.app?.modules?.dashboardManager?.returnToDashboard?.();
    window.history.pushState({}, '', '/');

    return { type: 'instant', text: '' };
  }

  // ========================================
  // EDGE CASE HANDLING
  // ========================================

  /**
   * Handle urgent advice request during onboarding
   */
  async _handleUrgentRequest() {
    this.subState = ORIENTATION_SUB_STATES.HANDLING_ADVICE;

    return {
      type: 'instant',
      text: "What's on your mind? I'll do my best to help.",
      messageType: 'system'
    };
  }

  // ========================================
  // PERSISTENCE
  // ========================================

  /**
   * Mirror the persisted step + data onto window.app.currentUser so the
   * dashboard's "Finish Orientation" card and the resume flow read the latest
   * value without a refetch. The router gate and FeatureToggleService also
   * read `onboardingStep` from the in-memory user, so keeping it fresh is
   * what makes dismiss-then-resume land on the correct phase.
   * @private
   */
  _mirrorPersistedState(step) {
    if (!window.app?.currentUser) return;
    window.app.currentUser.onboardingStep = step;
    window.app.currentUser.onboardingData = { ...this.onboardingData };
  }

  /**
   * Persist phase transition to backend
   */
  async _persistPhaseTransition(nextPhase) {
    const step = PHASE_TO_STEP[nextPhase];
    if (!step) {
      console.warn('[OrientationMode] No step mapping for phase:', nextPhase);
      return;
    }

    console.log('[OrientationMode] Persisting phase transition:', { nextPhase, step, data: this.onboardingData });
    await this._persistWithRetry(step, this.onboardingData);
  }

  /**
   * Persist step data to backend
   * @param {Object} data - Data to merge into onboardingData
   * @param {string} [stepOverride] - Optional step name to persist as (defaults to current phase)
   */
  async _persistStepData(data, stepOverride) {
    // Merge into local state so subsequent persists don't overwrite
    Object.assign(this.onboardingData, data);

    const step = stepOverride || PHASE_TO_STEP[this.currentPhase];
    await this._persistWithRetry(step, { ...this.onboardingData });
  }

  /**
   * Persist an onboarding step to the backend with bounded retry.
   *
   * Failure policy: never block the conversation — in-memory state stays
   * authoritative so the flow continues — but don't stay silent either.
   * After the retry budget is exhausted, surface a one-per-streak system
   * notice warning that a reload may lose recent answers.
   *
   * @param {string} step - Onboarding step name to persist
   * @param {Object} payload - onboardingData snapshot to persist
   * @returns {Promise<boolean>} true if persisted (and state mirrored)
   */
  async _persistWithRetry(step, payload) {
    let API;
    try {
      ({ API } = await import('../../core/api.js'));
    } catch (error) {
      console.error('[OrientationMode] Failed to load API module:', error);
      this._notifyPersistFailure();
      return false;
    }

    let lastError = null;
    for (let attempt = 1; attempt <= ORIENTATION_PERSIST_ATTEMPTS; attempt++) {
      try {
        await API.updateGuestOnboardingStep(step, payload);
        this._mirrorPersistedState(step);
        this._persistFailureNotified = false;
        return true;
      } catch (error) {
        lastError = error;
        if (attempt < ORIENTATION_PERSIST_ATTEMPTS) {
          await new Promise(resolve => setTimeout(resolve, this._persistRetryDelayMs * attempt));
        }
      }
    }

    console.error('[OrientationMode] Failed to persist onboarding state after retries:', { step, error: lastError });
    this._notifyPersistFailure();
    return false;
  }

  /**
   * Surface a non-blocking notice that onboarding progress is not saving.
   * One notice per failure streak — a later successful persist re-arms it.
   */
  _notifyPersistFailure() {
    if (this._persistFailureNotified) return;
    this._persistFailureNotified = true;

    const messageList = window.modeOrchestrator?.chatManager?.messageList;
    messageList?.addAssistantMessage(
      "I'm having trouble saving your progress right now. We can keep going — but if you reload the page, your recent answers may not be remembered.",
      { messageType: 'system', customClass: 'message--orientation' }
    );
  }

  /**
   * Rebuild ephemeral state from persisted data
   */
  async _rebuildEphemeralState() {
    // A converted guest still parked on ACCOUNT_PROMPT has not yet seen (or has
    // not yet dismissed) the ACCOUNT_SETUP "your account is all set" beat —
    // clicking its Continue is what advances the phase. So the phase IS the
    // acknowledgement, and this stays correct under repeated reloads.
    //
    // Derived from `convertedAt` rather than a written-once flag on purpose:
    // `convertedAt` is stamped by every guest→account path (both
    // guestConversionService methods AND passport's guest-repair branch, which
    // is what a Google sign-in takes when the session carries no live beta
    // admission). A flag set by only one of those paths silently loses the beat
    // on the others — the auto-advance below is unannounced, so the failure is
    // invisible.
    if (
      this.currentPhase === ORIENTATION_PHASES.ACCOUNT_PROMPT &&
      !window.app?.isGuest &&
      window.app?.currentUser?.convertedAt
    ) {
      this._showAccountBeat = true;
    }

    // Authenticated users who did NOT just convert do not belong on
    // ACCOUNT_PROMPT — auto-advance to TRANSITION. Layered after the beat
    // detector so conversions still get their acknowledgement.
    if (
      this.currentPhase === ORIENTATION_PHASES.ACCOUNT_PROMPT &&
      !window.app?.isGuest &&
      !this._showAccountBeat
    ) {
      console.log('[OrientationMode] Authenticated user at ACCOUNT_PROMPT, advancing to TRANSITION');
      this.currentPhase = ORIENTATION_PHASES.TRANSITION;
      this.currentStepIndex = 0;
      await this._persistPhaseTransition(ORIENTATION_PHASES.TRANSITION);
    }

    // Rebuild Love Languages insight from persisted onboarding data
    if (this.onboardingData.loveLangsInsightId) {
      this._rebuildInsightFromPersistedData();
    }

    // Handle edge case: questionnaire completed in DISCOVERY but phase transition didn't persist
    // If we're in DISCOVERY but have loveLangsInsightId, we should be in INSIGHT
    if (this.currentPhase === ORIENTATION_PHASES.DISCOVERY && this.onboardingData.loveLangsInsightId) {
      console.log('[OrientationMode] Detected completed questionnaire in DISCOVERY, advancing to INSIGHT');
      this.currentPhase = ORIENTATION_PHASES.INSIGHT;
      this.currentStepIndex = 0; // Start at processing step
      // Persist the corrected phase
      await this._persistPhaseTransition(ORIENTATION_PHASES.INSIGHT);
    }

    // Skip directly to insight_display step when resuming with insight already generated
    // This prevents showing the "processing" message again on return
    if (this.currentPhase === ORIENTATION_PHASES.INSIGHT && this.onboardingData.loveLangsInsightId && this.currentInsight) {
      console.log('[OrientationMode] Resuming INSIGHT with existing insight, skipping to insight_display');
      const insightDisplayIndex = ORIENTATION_CONTENT[ORIENTATION_PHASES.INSIGHT]?.steps?.findIndex(s => s.id === 'insight_display');
      if (insightDisplayIndex >= 0) {
        this.currentStepIndex = insightDisplayIndex;
      }
    }
  }

  // ========================================
  // HELPER METHODS
  // ========================================

  /**
   * Check if a button action is valid for the current phase.
   * Safety net for stale buttons from prior phases.
   * @param {string} action - The action to validate
   * @returns {boolean} True if action is valid for current phase
   * @private
   */
  _isActionValidForCurrentPhase(action) {
    if (GLOBAL_VALID_ACTIONS.includes(action)) return true;
    const validActions = VALID_ACTIONS_BY_PHASE[this.currentPhase];
    if (!validActions) return true; // Unknown phase, allow
    return validActions.includes(action);
  }

  /**
   * Disable all orientation interactive elements in the chat.
   * Layer 1 of stale button protection — prevents clicks and provides visual signal
   * that previous options are no longer available.
   * @private
   */
  _disableAllOrientationInteractives() {
    // Disable all orientation action buttons
    document.querySelectorAll('.action-btn[data-action^="orientation:"]').forEach(btn => {
      btn.disabled = true;
      if (!btn.classList.contains('action-btn--hiding')) {
        btn.classList.add('action-btn--clicked');
      }
    });
    // Disable questionnaire/insight artifact clicks
    document.querySelectorAll('.artifact').forEach(artifact => {
      artifact.style.pointerEvents = 'none';
      artifact.classList.add('artifact--stale');
    });
  }


  /**
   * Get the current step configuration.
   * Resolves ACCOUNT_PROMPT sub-branches (GUEST_CONTINUES / ACCOUNT_SETUP)
   * when the user has already made a branch choice.
   * @returns {Object|null} Current step config or null
   */
  _getCurrentStep() {
    // Resolve sub-branch content for ACCOUNT_PROMPT
    if (this.currentPhase === ORIENTATION_PHASES.ACCOUNT_PROMPT && this.onboardingData.branch) {
      const branchContent = this.onboardingData.branch === 'defer'
        ? ORIENTATION_CONTENT.GUEST_CONTINUES
        : ORIENTATION_CONTENT.ACCOUNT_SETUP;
      return branchContent?.steps?.[0] || null;
    }

    const phaseContent = ORIENTATION_CONTENT[this.currentPhase];
    return phaseContent?.steps?.[this.currentStepIndex] || null;
  }

  /**
   * Get the current header key based on phase and step
   */
  _getCurrentHeaderKey() {
    const step = ORIENTATION_CONTENT[this.currentPhase]?.steps?.[this.currentStepIndex];

    // Questionnaire-skipped fork, following the same `skipped*` step-key
    // convention as the copy and button forks (skippedInsightTrailingMessage,
    // skippedPostArtifactButtons, skippedButtonGroups).
    const explicitHeader = (this.onboardingData.skippedLoveLanguages && step?.skippedHeader)
      ? step.skippedHeader
      : step?.header;

    // Use step's explicit header if defined
    if (explicitHeader) {
      // Find the key in PHASE_HEADERS that matches this value
      const headerKey = Object.entries(PHASE_HEADERS).find(([key, value]) => value === explicitHeader);
      if (headerKey) {
        return headerKey[0];
      }
    }

    // Fallback to phase-based headers
    switch (this.currentPhase) {
      case ORIENTATION_PHASES.INTRODUCTION:
        return this.onboardingData.firstName ? 'INTRODUCTION_NAMED' : 'INTRODUCTION_START';
      case ORIENTATION_PHASES.DISCOVERY:
        return 'DISCOVERY';
      case ORIENTATION_PHASES.INSIGHT:
        return 'INSIGHT';
      case ORIENTATION_PHASES.ACCOUNT_PROMPT:
        if (this.onboardingData.branch === 'account') return 'ACCOUNT_SETUP';
        if (this.onboardingData.branch === 'defer') return 'GUEST_CONTINUES';
        return 'ACCOUNT_PROMPT';
      case ORIENTATION_PHASES.TRANSITION:
        return 'TRANSITION';
      default:
        return 'INTRODUCTION_START';
    }
  }

  /**
   * Update input placeholder based on current state
   */
  _updatePlaceholder() {
    const step = ORIENTATION_CONTENT[this.currentPhase]?.steps?.[this.currentStepIndex];

    if (step?.inputPlaceholder) {
      this._setInputPlaceholder(step.inputPlaceholder);
    } else {
      this._setInputPlaceholder(OrientationMode.PLACEHOLDERS.DEFAULT);
    }
  }

  /**
   * Set custom placeholder on input
   */
  _setInputPlaceholder(placeholder) {
    if (window.app?.modules?.input) {
      window.app.modules.input.setPlaceholder(placeholder);
    }
  }

  /**
   * Clear custom placeholder
   */
  _clearInputPlaceholder() {
    if (window.app?.modules?.input) {
      window.app.modules.input.clearCustomPlaceholder();
    }
  }

  // ========================================
  // EVENT LISTENERS
  // ========================================

  /**
   * Set up event listeners for orientation flow
   */
  _setupEventListeners() {
    if (!window.modeOrchestrator?.eventBus) return;

    // Self-idempotent: clear any previously-bound handlers before re-binding.
    // Defensive against `modeOrchestrator.returnToDashboard()` skipping onExit
    // — without this, a dismiss → resume cycle would stack duplicate listeners.
    this._cleanupEventListeners();

    const eventBus = window.modeOrchestrator.eventBus;

    // Questionnaire completion
    this._boundHandlers.questionnaireComplete = (data) => {
      if (this.currentPhase && this.questionnaireActive) {
        this._handleQuestionnaireComplete(data);
      }
    };
    eventBus.on('questionnaire:completed', this._boundHandlers.questionnaireComplete);

    // Register orientation action handler with ActionRouter
    // This allows OrientationMode to handle orientation:* actions through the standard routing
    window.modeOrchestrator.actionRouter.register('orientation', (data) => {
      const action = data.action.split(':')[1];
      window.modeOrchestrator.handleUserMessage('', {
        metadata: { action }
      });
    });

    // Continuation-only hook for the conversion gate on `complete_foundation`.
    // Never reached by a click: orientation buttons dispatch under the
    // `orientation:` prefix, so executeAction routes them to the handler above.
    // This exists purely so app._consumeConversionIntent can resume the action
    // through the same ActionRouter path every other gated action uses.
    window.modeOrchestrator.actionRouter.register('complete_foundation', async () => {
      await this._transitionToFoundation();
    });

    // Account creation complete - trigger via handleUserMessage for proper rendering.
    // Phase-scoped: this is the ACCOUNT_PROMPT beat's continuation. A conversion
    // from a later phase (the TRANSITION gate, or the demo's conclusion) is
    // continued by its own intent — rendering the account-setup beat on top of it
    // would drop the user back into a step they had already passed.
    this._boundHandlers.accountCreated = async (data) => {
      if (this.currentPhase !== ORIENTATION_PHASES.ACCOUNT_PROMPT) {
        console.log('[OrientationMode] Account created outside ACCOUNT_PROMPT — intent owns the continuation');
        return;
      }
      console.log('[OrientationMode] Account created event received:', data);

      // Update branch state
      this.onboardingData.branch = 'account';
      await this._persistStepData({ branch: 'account' });

      // Trigger the continuation via the normal message flow
      // This ensures the response is properly rendered
      window.modeOrchestrator.handleUserMessage('', {
        metadata: { action: 'account_created' }
      });
    };
    eventBus.on('auth:account-created', this._boundHandlers.accountCreated);

    // Guest dismissed the conversion overlay via "Try it free" — show
    // guest-continues content. Same phase scoping: only the ACCOUNT_PROMPT ask
    // broadcasts (app routes an intent-bearing dismissal to its own restore),
    // and rendering GUEST_CONTINUES at TRANSITION would rewind the flow.
    this._boundHandlers.conversionDismissed = async () => {
      if (this.currentPhase !== ORIENTATION_PHASES.ACCOUNT_PROMPT) return;
      this.onboardingData.branch = 'defer';
      await this._persistStepData({ branch: 'defer' });
      const response = await this._showGuestContinuesContent();
      // Reached from the overlay dismissal, not a user turn — no bubble exists to
      // set the reading frame, so pin this message itself.
      await this._displayOrientationResponse(response, { scroll: true });
    };
    eventBus.on('auth:conversion-dismissed', this._boundHandlers.conversionDismissed);

    // Panel closed - check if current step should advance on panel close
    this._boundHandlers.panelClosed = async (data) => {
      const currentStep = this._getCurrentStep();
      if (currentStep?.advanceOnPanelClose &&
          currentStep.advanceOnPanelClose === data?.contentType) {
        console.log('[OrientationMode] Panel closed, advancing step');
        const response = await this._advanceStep();
        await this._displayOrientationResponse(response);
      } else if (
        data?.contentType === 'questionnaire' &&
        this.questionnaireActive &&
        currentStep?.showQuestionnaireArtifact &&
        currentStep?.postArtifactButtons?.length
      ) {
        // Questionnaire closed without completing or skipping (questionnaireActive is
        // cleared by both completion and skip) → restore Begin/Skip in the input so the
        // user can resume or skip. The artifact card remains in the transcript too.
        this._renderPostArtifactButtons(
          currentStep.postArtifactButtons,
          ORIENTATION_QUESTIONNAIRE_BUTTON_OPTIONS
        );
      }
    };
    eventBus.on('right-panel:closed', this._boundHandlers.panelClosed);
  }

  /**
   * Clean up event listeners
   */
  _cleanupEventListeners() {
    if (!window.modeOrchestrator?.eventBus) return;

    const eventBus = window.modeOrchestrator.eventBus;

    if (this._boundHandlers.questionnaireComplete) {
      eventBus.off('questionnaire:completed', this._boundHandlers.questionnaireComplete);
    }

    // Unregister orientation action handlers from ActionRouter
    window.modeOrchestrator?.actionRouter?.unregister('orientation');
    window.modeOrchestrator?.actionRouter?.unregister('complete_foundation');

    if (this._boundHandlers.accountCreated) {
      eventBus.off('auth:account-created', this._boundHandlers.accountCreated);
    }

    if (this._boundHandlers.conversionDismissed) {
      eventBus.off('auth:conversion-dismissed', this._boundHandlers.conversionDismissed);
    }

    if (this._boundHandlers.panelClosed) {
      eventBus.off('right-panel:closed', this._boundHandlers.panelClosed);
    }

    this._boundHandlers = {};
  }

  // ========================================
  // ORIENTATION UI STATE
  // ========================================

  /**
   * Emit UI state change event for PageManager
   * @param {'initial' | 'chat'} state - Target UI state
   */
  _emitUIState(state) {
    console.log('[OrientationMode] Emitting UI state:', state);
    this._headerVisible = state === 'chat';
    if (window.modeOrchestrator?.eventBus) {
      window.modeOrchestrator.eventBus.emit('orientation:ui-state', { state });
    }
  }

  /**
   * Show thinking indicator with optional text.
   * Uses the default bars presentation (no waiting-phase logo) during orientation.
   * @param {string} [text] - Optional indicator text
   */
  _showThinkingIndicator(text) {
    if (window.app?.modules?.chatManager) {
      window.app.modules.chatManager.activateThinkingIndicator(text);
    }
  }

  /**
   * Hide thinking indicator
   */
  _hideThinkingIndicator() {
    if (window.app?.modules?.chatManager) {
      window.app.modules.chatManager.deactivateThinkingIndicator();
    }
  }

  /**
   * Render centered logo at top of chat area
   * The logo scrolls with messages as new content is added
   */
  _renderOrientationLogo() {
    // Check if logo already exists
    if (document.querySelector('.orientation-logo')) {
      return;
    }

    const logo = document.createElement('div');
    logo.className = 'orientation-logo';
    logo.innerHTML = `
      <svg class="orientation-logo__image" width="140" height="59">
        <use href="#logo-encouple"/>
      </svg>
    `;

    // Insert at top of message list
    const messageList = document.querySelector('.messages__list');
    if (messageList) {
      messageList.prepend(logo);
      console.log('[OrientationMode] Logo rendered in chat area');
    } else {
      console.warn('[OrientationMode] Could not find .messages__list to render logo');
    }
  }

  /**
   * Remove the orientation logo from chat area
   * Called when orientation completes or on cleanup
   */
  _removeOrientationLogo() {
    const logo = document.querySelector('.orientation-logo');
    if (logo) {
      logo.remove();
      console.log('[OrientationMode] Logo removed from chat area');
    }
  }
}
